import React, { useContext, useState } from 'react'
import { Text, View } from 'oio-react'
import { Link, useRouteMatch } from 'react-router-dom'
import uiConstants, { LAYOUT_BOX } from 'config/constants/ui'
import { adminManageContentTypesUrl, initiativeUrl } from 'config/constants/urls'
import { useInitiativeList, useOrganization } from 'src/core/graphql/hooks'
import EventListBlock from 'src/sites/kits/Events/components/ListBlock'
import ListItemRowSmall from 'src/sites/kits/Initiative/components/ListItemRowSmall'
import InitiativeMoreButton from 'src/sites/kits/Initiative/components/MoreButton'
import ObjectCreateControl from 'src/sites/kits/Object/components/CreateControl'
import ObjectStatusBanner from 'src/sites/kits/Object/components/StatusBanner'
import PeopleAsideBlock from 'src/sites/kits/People/components/AsideBlock'
import { EmptyContentBlock, LayoutContainer, LayoutContainerInner, ViewModeSwitchControl,
   Title, Titlebar } from 'src/sites/kits/UI'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'
import { useGlobalState } from 'src/sites/state'

const EventHome = () => {
   const match = useRouteMatch()
   const { theme } = useGlobalState()
   const [viewMode, setViewMode] = useState(uiConstants.viewModes.GRID)
   const isBoxGL = theme.tmpGlobalLayout === LAYOUT_BOX

   const { initiative: page } = useContext(InitiativeHierarchyContext)
   const { organization } = useOrganization()
   const eventInitiativeType = organization.initiativeTypes
      .find(t => t.id === page.dynamicPageType.id)

   const groupTypeIds = organization.initiativeTypes
      .filter(t => t.class === 'group')
      .map(t => t.id)

   // Get groups that have events
   const { initiativeList: groupList } = useInitiativeList({
      archived: 'exclude',
      drafts: 'include',
      hasChildrenOfTypeId: eventInitiativeType?.id,
      typeIds: groupTypeIds,
      levels: 'all',
      limit: 25
   }, {
      skip: !eventInitiativeType
   })

   const gridNumColumns = isBoxGL
      ? '1[a] 2[b] 3[c] 3[d] 2[e] 2[f]'
      : '1[a] 2[b] 3[c-f]' // Default - Header Global Layout

   return (
      <LayoutContainer maxWidth="1200px">
         <LayoutContainerInner>
            <ObjectStatusBanner />
            <View
               display="flex"
               flexFlow={isBoxGL ? 'column[a-d] row[e-f]' : 'column[a-c] row[d-f]'}>
               <View
                  flex="1 1 auto"
                  height="100%"
                  minHeight="100vh"
                  borderRight={isBoxGL
                     ? `1px solid ${theme.tmpColumnBorderColor}[e-f]`
                     : `1px solid ${theme.tmpColumnBorderColor}[d-f]`}
                  paddingTop={isBoxGL && '10px[d] 30px[e-f]'}
                  paddingLeft={theme.tmpAppOuterPaddingHorizontal}
                  paddingRight={theme.tmpAppInnerPaddingHorizontal}>
                  <Titlebar size="lg">
                     <Title>{page.name}</Title>
                     <View display="flex">
                        <ViewModeSwitchControl
                           currentViewMode={viewMode}
                           onListButtonClick={() => setViewMode(uiConstants.viewModes.ROW)}
                           onGridButtonClick={() => setViewMode(uiConstants.viewModes.GRID)}
                        />
                        {organization.currentUserCanEdit && (
                           <View display="block" width="10px" height="30px" />
                        )}
                        <ObjectCreateControl
                           initiativeTypes={[eventInitiativeType]}
                           parentInitiativeId={page.id}
                        />
                        {organization.currentUserCanEdit && (
                           <View display="block" width="10px" height="30px" />
                        )}
                        <InitiativeMoreButton
                           buttonRounded
                           buttonSize="sm"
                           buttonWidth="30px"
                           editUrl={`${match.url}/-/edit`}
                           popoverAnchorOriginVertical="top"
                           showInitiativeName
                        />
                     </View>
                  </Titlebar>
                  {!eventInitiativeType.enabled && (
                     <EmptyContentBlock
                        height="90px"
                        message={(
                           <View>
                              {`${eventInitiativeType.namePlural} have been disabled by the Admins of ${organization.name}.`}
                              <br />
                              <Link to={adminManageContentTypesUrl}>
                                 <Text
                                    weight="medium"
                                    style={{ display: 'inline' }}
                                    size={theme.tmpSubBodyTextSize}
                                    color={theme.tmpContentTextColor}>
                                    <u>Click here</u>
                                 </Text>
                              </Link>
                              &nbsp;to re-enable them.
                           </View>
                        )}
                     />
                  )}
                  {eventInitiativeType.enabled && (
                     <EventListBlock
                        initiativeTypeId={eventInitiativeType.id}
                        titleSize="lg"
                        viewMode={viewMode}
                        viewModeGridNumColumns={gridNumColumns}
                     />
                  )}
               </View>
               <View
                  flex="0 0 auto"
                  width={isBoxGL
                     ? '100%[a-d] 380px[e-f]'
                     : '100%[a-c] 360px[d-f]'
                  }
                  paddingLeft={theme.tmpAppInnerPaddingHorizontal}
                  paddingRight={theme.tmpAppOuterPaddingHorizontal}
                  minHeight="100%"
                  paddingTop={isBoxGL && '10px[d] 30px[e-f]'}
                  borderTop={isBoxGL
                     ? `1px solid ${theme.tmpColumnBorderColor}[a-d]`
                     : `1px solid ${theme.tmpColumnBorderColor}[a-c]`
                  }>
                  <View paddingTop={theme.tmpSpacingTop}>
                     {groupList.items.length > 0 && (
                        <View marginBottom="30px">
                           <Text size="3" weight="medium" color={theme.tmpContentTitleColor}>
                              Groups with Events
                           </Text>
                           <View padding="15px 0px 30px 0px">
                              <Text size="2" color="#777">
                                 {'The following Groups in this Community have event pages of their own:'}
                              </Text>
                           </View>
                           {groupList.items.map(initiative => (
                              <ListItemRowSmall
                                 key={initiative.id}
                                 coverUrl={initiative.coverMedia?.file.thumbnailUrlW480}
                                 dateLastUpdated={initiative.dateLastUpdated}
                                 initiativeTypeIconName="group"
                                 linkTo={`${initiativeUrl(initiative)}/-/events`}
                                 name={initiative.name}
                                 privacy={initiative.privacy}
                                 showStats={false}
                                 typeSlug={initiative.type.slug}
                              />
                           ))}
                        </View>
                     )}
                     <PeopleAsideBlock />
                  </View>
               </View>
            </View>
         </LayoutContainerInner>
      </LayoutContainer>
   )
}

export default EventHome
